/**
 * Economy formulas — anything that turns counts into prices or throughput
 * caps. Kept separate from `market.ts` (which is about resource prices) so
 * it doesn't drag in the resource catalog when only counting goes up.
 *
 * All formulas are pure functions of the count you already have. That lets
 * both server (for validation/deduction) and client (for UI preview) agree
 * on the price with no handshake — the number shown in the Commission
 * panel is the number the server will deduct.
 */
export const DRONE_BASE_COST = 30;
/**
 * Multiplier applied per existing drone when pricing the *next* one. At
 * 1.25× and a ₡30 base the schedule reads:
 *   n=0: 30   n=2: 47   n=5: 92   n=10: 280   n=20: 2.6k   n=30: 24k
 * Cheap enough that your first commission is affordable after one or two
 * sales; steep enough that building a fleet of 30 is a real achievement.
 */
export const DRONE_COST_GROWTH = 1.25;
/**
 * Credit cost to commission the next freighter drone given the number you
 * already own. Defaults match the default station's two seeded drones, so
 * the first *manual* commission is priced at ₡47.
 *
 * Pass `overrides` (typically derived from a `TuningStore`) to use live
 * admin-adjusted values instead of the baseline constants.
 */
export function droneCost(existingDroneCount, overrides) {
    const base = overrides?.base ?? DRONE_BASE_COST;
    const growth = overrides?.growth ?? DRONE_COST_GROWTH;
    const raw = base * Math.pow(growth, existingDroneCount);
    return Math.ceil(raw);
}
/**
 * Starting credit balance for a brand-new player. Enough to afford the first
 * commission or two outright, so the tutorial path doesn't require staring
 * at an empty Trade Port waiting for the seeded drones to earn bus fare.
 */
export const STARTING_CREDITS = 120;
/**
 * Fraction of a structure's catalog cost that's refunded when the player
 * tears it down (machines, belts, junctions, drones). 80% is generous
 * enough that experimentation with layouts feels safe — you're docked a
 * 20% "scrap fee" rather than punished for redesigning. The remaining
 * 20% acts as a soft tax that prevents perfectly-zero-cost reshuffling
 * loops (e.g. building a belt to cross a tile, then refunding the cost).
 */
export const STRUCTURE_REFUND_RATE = 0.8;
/**
 * Compute the refund credited when a structure built for `cost` credits
 * is torn down. Floors to whole credits — the 20% scrap fee always sticks.
 */
export function structureRefund(cost) {
    if (!Number.isFinite(cost) || cost <= 0)
        return 0;
    return Math.floor(cost * STRUCTURE_REFUND_RATE);
}
// ── P5: drone power draw ───────────────────────────────────────────────────
/**
 * Constant kW pulled from the grid for every drone, regardless of state.
 * Modeled as a small hover/sensor draw — even parked drones keep their
 * electronics warm.
 */
export const DRONE_IDLE_DRAW_KW = 0.1;
/**
 * Additional kW pulled while a drone is actively moving, loading, or
 * unloading. Stacks on top of the idle draw, so a flying freighter pulls
 * `DRONE_IDLE_DRAW_KW + DRONE_ACTIVE_DRAW_KW` from the bus.
 *
 * Together they create a deliberate cascade-failure path: a fuel shortage
 * → power deficit → drones throttle → fuel deliveries slow → deeper deficit.
 * That's the kind of feedback loop that makes the kW grid feel *alive*.
 */
export const DRONE_ACTIVE_DRAW_KW = 0.5;
//# sourceMappingURL=economy.js.map